/**
 * StrumentiACanoneTab — la linguetta «Strumenti a canone» della pagina dei prezzi (atlante cap. 251, tappa 4).
 *
 * Per ogni strumento: il canone in grande, la quota e l'extra in una frase, cosa fa al tetto, quante aziende lo hanno e
 * come lo pagano, i prezzi su Stripe con le versioni di prima. «Cambia prezzi e regole» apre la finestra che crea i
 * prezzi nuovi. Gli strumenti nuovi non nascono qui: nascono dal codice, perché qualcuno li deve anche contare.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Collapse, Paper, Stack, Typography } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import { soloAConsumo, type StrumentoACanone } from '@shared/strumenti/listino';
import { righeDiConsumo } from '@shared/strumenti/consumoDellAzienda';
import { listTenants, type TenantConsoleListItem } from '../../services/tenantService';
import { leggiListino, type EsitoModificaStrumento } from '../../services/strumentiService';
import { DialogoModificaStrumento } from './DialogoModificaStrumento';
import { AL_TETTO, categoriaDiPagamento, condizioniDette, data, euro, fraseConsumo } from './testiStrumenti';

interface Diffusione {
  /** Per ogni modo di pagare («paga», «regalo»…), quante aziende. */
  perModo: Map<string, number>;
  /** Quante lo hanno nell'abbonamento (restano alle condizioni di prima se il prezzo cambia). */
  abbonate: number;
  /** Quante sono ancora su una versione di prezzo precedente. */
  alPrezzoDiPrima: number;
}

function diffusione(s: StrumentoACanone, aziende: readonly TenantConsoleListItem[], oraMs: number): Diffusione {
  const perModo = new Map<string, number>();
  let abbonate = 0;
  let alPrezzoDiPrima = 0;
  const listino = new Map([[s.capability, s]]);
  for (const a of aziende) {
    const riga = righeDiConsumo(a, listino, null, oraMs)[0];
    if (!riga) continue;
    const modo = categoriaDiPagamento(riga);
    perModo.set(modo, (perModo.get(modo) ?? 0) + 1);
    const stato = (a as { billing?: { strumenti?: Record<string, { attivo?: boolean; price_canone_id?: string }> } }).billing?.strumenti?.[s.capability];
    if (stato?.attivo) {
      abbonate += 1;
      if (stato.price_canone_id && stato.price_canone_id !== s.stripe.live?.price_canone_id) alPrezzoDiPrima += 1;
    }
  }
  return { perModo, abbonate, alPrezzoDiPrima };
}

const SchedaStrumento: React.FC<{ s: StrumentoACanone; d: Diffusione; onModifica: () => void }> = ({ s, d, onModifica }) => {
  const [stripeAperto, setStripeAperto] = useState(false);
  const accese = [...d.perModo.values()].reduce((a, b) => a + b, 0);
  const live = s.stripe.live;
  return (
    <Paper variant="outlined" sx={{ p: 3, borderColor: tokens.color.line, borderRadius: `${tokens.radius.lg}px`, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: tokens.color.graphite }}>{s.etichetta}</Typography>
        <SoftChip label={s.in_vendita ? 'in vendita' : 'fuori vendita'} tone={s.in_vendita ? 'success' : 'default'} />
      </Stack>

      <Box>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography sx={{
            fontFamily: tokens.font.display, fontWeight: 600, fontSize: '2.6rem', lineHeight: 1, letterSpacing: '-0.02em',
            color: tokens.color.graphite, fontVariantNumeric: 'tabular-nums', fontVariationSettings: '"WONK" 0, "SOFT" 30',
          }}>
            {soloAConsumo(s) ? 'A consumo' : euro(s.canone_mensile_eur)}
          </Typography>
          {!soloAConsumo(s) && <Typography sx={{ color: tokens.color.ash }}>al mese</Typography>}
        </Stack>
        <Typography sx={{ mt: 1, color: tokens.color.graphite }}>{fraseConsumo(s)}.</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: tokens.color.ash }}>Cosa fa: {AL_TETTO[s.al_tetto]}.</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: tokens.color.ash }}>
          Tetto di partenza: {s.tetto_predefinito_eur === null ? 'pari al canone' : `${euro(s.tetto_predefinito_eur)} al mese`}.
          {s.richiede ? ` Si attiva solo con «${s.richiede}» nel piano.` : ''}
        </Typography>
      </Box>

      <Box sx={{ borderTop: `1px solid ${tokens.color.lineSoft}`, pt: 1.5 }}>
        <Typography variant="body2" sx={{ color: tokens.color.graphite, fontWeight: 600 }}>
          {accese === 0 ? 'Nessuna azienda lo ha acceso.' : `Acceso in ${accese} ${accese === 1 ? 'azienda' : 'aziende'}`}
        </Typography>
        {accese > 0 && (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
            {[...d.perModo.entries()].map(([modo, n]) => <SoftChip key={modo} label={`${modo}: ${n}`} tone="default" />)}
          </Stack>
        )}
        {d.alPrezzoDiPrima > 0 && (
          <Typography variant="body2" sx={{ mt: 1, color: tokens.color.warning }}>
            {d.alPrezzoDiPrima === 1 ? 'Un’azienda paga ancora il prezzo di prima.' : `${d.alPrezzoDiPrima} aziende pagano ancora il prezzo di prima.`}
          </Typography>
        )}
      </Box>

      <Box>
        <Button size="small" onClick={() => setStripeAperto((v) => !v)} endIcon={stripeAperto ? <ExpandLess /> : <ExpandMore />}
          sx={{ textTransform: 'none', color: tokens.color.ash, px: 0 }}>
          Prezzi su Stripe
        </Button>
        <Collapse in={stripeAperto}>
          {(['live', 'test'] as const).map((amb) => {
            const ids = s.stripe[amb];
            return (
              <Box key={amb} sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: tokens.color.ash }}>{amb === 'live' ? 'Soldi veri' : 'Prove'}</Typography>
                {ids ? (
                  <Box sx={{ fontFamily: tokens.font.mono, fontSize: '0.75rem', color: tokens.color.graphite, lineHeight: 1.7 }}>
                    <div>canone {ids.price_canone_id}</div>
                    {ids.price_consumo_id && <div>consumo {ids.price_consumo_id}</div>}
                    {ids.prezzi_precedenti.map((v) => (
                      <div key={v.price_canone_id + (v.price_consumo_id ?? '')} style={{ color: tokens.color.mist }}>
                        {v.fino_al_ms ? `fino al ${data(v.fino_al_ms)}: ` : 'prima: '}{condizioniDette(v)}
                      </div>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: tokens.color.mist }}>non ancora preparati</Typography>
                )}
              </Box>
            );
          })}
        </Collapse>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <Button variant="outlined" onClick={onModifica} disabled={!live} sx={{ textTransform: 'none', minHeight: 40 }}>
          Cambia prezzi e regole
        </Button>
        {!live && <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: tokens.color.mist }}>Prima vanno preparati i prezzi su Stripe con lo script.</Typography>}
      </Box>
    </Paper>
  );
};

export const StrumentiACanoneTab: React.FC = () => {
  const [listino, setListino] = useState<Map<string, StrumentoACanone> | null>(null);
  const [aziende, setAziende] = useState<TenantConsoleListItem[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [inModifica, setInModifica] = useState<string | null>(null);
  const [esito, setEsito] = useState<EsitoModificaStrumento | null>(null);

  const carica = async () => {
    setErrore(null);
    try {
      const [l, a] = await Promise.all([leggiListino(), listTenants()]);
      setListino(l);
      setAziende(a);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void carica(); }, []);

  const ora = Date.now();
  const diffusioni = useMemo(
    () => new Map([...(listino?.values() ?? [])].map((s) => [s.capability, diffusione(s, aziende, ora)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listino, aziende],
  );

  if (errore) return <Alert severity="error">{errore}</Alert>;
  if (!listino) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>;

  const inModificaS = inModifica ? listino.get(inModifica) : undefined;
  return (
    <Box>
      <Typography variant="body2" sx={{ color: tokens.color.ash, mb: 2.5, maxWidth: 720 }}>
        Tutti i prezzi sono IVA esclusa: l’IVA la aggiunge Stripe in fattura. Un prezzo cambiato qui vale per chi attiva
        lo strumento da quel momento: chi lo ha già resta al suo prezzo, che il sistema ricorda. Gli strumenti nuovi
        nascono dal codice, perché vanno anche contati.
      </Typography>
      {esito && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEsito(null)}>
          {esito.prezzi_creati.length > 0
            ? `Prezzi nuovi creati su Stripe (${esito.ambiente === 'live' ? 'soldi veri' : 'prove'}): ${esito.prezzi_creati.join(', ')}.`
            : 'Regole salvate.'}
          {esito.altro_ambiente_da_riallineare && ' L’ambiente di prova ha ancora i prezzi di prima: si riallinea lanciando setup-stripe-strumenti.cjs --apply con la chiave di prova.'}
        </Alert>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {[...listino.values()].map((s) => (
          <SchedaStrumento key={s.capability} s={s} d={diffusioni.get(s.capability)!} onModifica={() => setInModifica(s.capability)} />
        ))}
      </Box>
      {inModificaS && (
        <DialogoModificaStrumento
          aperto
          onChiudi={() => setInModifica(null)}
          onSalvato={(r) => { setEsito(r); setInModifica(null); void carica(); }}
          strumento={inModificaS}
          abbonate={diffusioni.get(inModificaS.capability)?.abbonate ?? 0}
        />
      )}
    </Box>
  );
};
